import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { importPlatformList } from '../../../api/cms/platform';

const ImportPlatformModal = ({ open, onClose, onSuccess, existingPlatforms }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [responseData, setResponseData] = useState([]);

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) setSelectedFile(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: '.json', // keep as-is to match your original usage
    multiple: false,
  });

  const normalizeArray = (result) => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result && result.game_syncs)) return result.game_syncs;
    return [];
  };

  const getErrorMessage = (row) => {
    if (!row) return undefined;

    if (row.error) {
      if (typeof row.error === 'string') return row.error;
      return row.error && row.error.message;
    }
    if (row.Error) {
      if (typeof row.Error === 'string') return row.Error;
      return row.Error && row.Error.message;
    }
    return undefined;
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const fileContent = await selectedFile.text();
      const existingCodes = existingPlatforms.map((p) => p.platform_code);
      const duplicates = normalizeArray(JSON.parse(fileContent)).filter((item) =>
        existingCodes.includes(item.platform_code),
      );

      if (duplicates.length > 0) {
        const duplicateCodes = duplicates.map((d) => d.platform_code).join(', ');
        throw new Error(`Import failed, records existed: ${duplicateCodes}`);
      }

      const result = await importPlatformList(selectedFile);
      const items = normalizeArray(result);
      setResponseData(items);

      const hasSuccess = items.some((it) => !getErrorMessage(it));
      if (hasSuccess) {
        onSuccess && onSuccess(); // refresh table
        toast.success('Import completed.');
      } else {
        toast.error('All rows failed to import.');
      }
    } catch (err) {
      const message =
        (err && err.response && err.response.data && err.response.data.message) ||
        err?.message ||
        'Import failed';
      setResponseData([
        {
          platform_code: '',
          platform_name: '',
          merchant_code: '',
          Error: message,
        },
      ]);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setSelectedFile(null);
    setResponseData([]);
    setUploading(false);
  };

  const handleClose = () => {
    reset();
    onClose && onClose();
  };

  const hasResults = responseData.length > 0;
  const successCount = responseData.filter((row) => !getErrorMessage(row)).length;
  const failCount = responseData.length - successCount;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Platform List</DialogTitle>
      <DialogContent>
        {!hasResults ? (
          <Box
            {...getRootProps()}
            border="2px dashed #ccc"
            borderRadius="4px"
            p={4}
            textAlign="center"
            bgcolor={isDragActive ? '#f0f0f0' : 'inherit'}
            style={{ cursor: 'pointer' }}
          >
            <input {...getInputProps()} />
            <Typography variant="body1">
              {selectedFile
                ? selectedFile.name
                : 'Drag & drop a JSON file here, or click to select'}
            </Typography>
          </Box>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1">Import Result</Typography>
              <Box>
                <Chip
                  label={`Success: ${successCount}`}
                  style={{ marginRight: 8 }}
                  color="primary"
                />
                <Chip label={`Failed: ${failCount}`} color="secondary" />
              </Box>
            </Box>

            <TableContainer component={Paper} style={{ maxHeight: 420 }}>
              <Table stickyHeader size="small" aria-label="import result table">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '28%' }}>Platform Name</TableCell>
                    <TableCell style={{ width: '18%' }}>Platform Code</TableCell>
                    <TableCell style={{ width: '18%' }}>Merchant Code</TableCell>
                    <TableCell style={{ width: '12%' }}>Status</TableCell>
                    <TableCell style={{ width: '24%' }}>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {responseData.map((item, idx) => {
                    const errMsg = getErrorMessage(item);
                    const isError = Boolean(errMsg);
                    return (
                      <TableRow key={idx}>
                        <TableCell>{item.platform_name || '-'}</TableCell>
                        <TableCell>{item.platform_code || '-'}</TableCell>
                        <TableCell>{item.merchant_code || '-'}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            style={{ fontWeight: 600, color: isError ? '#d32f2f' : '#2e7d32' }}
                          >
                            {isError ? 'Failed' : 'Success'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={isError ? 'error' : 'textSecondary'}>
                            {errMsg || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        {!hasResults ? (
          <Button
            onClick={handleUpload}
            color="primary"
            disabled={!selectedFile || uploading}
            variant="contained"
            startIcon={uploading ? <CircularProgress size={18} /> : undefined}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        ) : (
          <Button onClick={reset} variant="outlined">
            New Import
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportPlatformModal;
